import { useState } from 'react'
import { Calendar, Clock } from 'lucide-react'

export interface DateRange {
  date_from?: string
  date_to?: string
  hour_from?: number
  hour_to?: number
  year?: string
  preset: string
}

const PRESETS: { label: string; key: string; getRange: () => Partial<DateRange> }[] = [
  { label: 'YTD', key: 'ytd', getRange: () => {
    const year = new Date().getFullYear().toString()
    return { year, date_from: `${year}-01-01`, date_to: new Date().toISOString().slice(0, 10) }
  }},
  { label: '2026', key: '2026', getRange: () => ({ year: '2026', date_from: '2026-01-01', date_to: '2026-12-31' }) },
  { label: '2025', key: '2025', getRange: () => ({ year: '2025', date_from: '2025-01-01', date_to: '2025-12-31' }) },
  { label: '2024', key: '2024', getRange: () => ({ year: '2024', date_from: '2024-01-01', date_to: '2024-12-31' }) },
  { label: 'Last 90 Days', key: 'last90', getRange: () => {
    const to = new Date()
    const from = new Date(to.getTime() - 90 * 24 * 60 * 60 * 1000)
    return { date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) }
  }},
  { label: 'Last 30 Days', key: 'last30', getRange: () => {
    const to = new Date()
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000)
    return { date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) }
  }},
  { label: 'Custom', key: 'custom', getRange: () => ({}) },
]

interface DateRangeFilterProps {
  value: DateRange
  onChange: (range: DateRange) => void
  showTimeFilter?: boolean
}

export function DateRangeFilter({ value, onChange, showTimeFilter = true }: DateRangeFilterProps) {
  const [showCustom, setShowCustom] = useState(value.preset === 'custom')

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Preset pills */}
      <div className="flex gap-1.5">
        {PRESETS.filter(p => p.key !== 'custom').map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => {
              setShowCustom(false)
              onChange({ ...p.getRange(), preset: p.key })
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              value.preset === p.key
                ? 'bg-butler-600 text-white shadow-brand-tinted'
                : 'bg-slate-100 text-ink-soft hover:bg-butler-50 hover:text-butler-700'
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setShowCustom(true)
            onChange({ ...value, preset: 'custom' })
          }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors inline-flex items-center gap-1 ${
            value.preset === 'custom'
              ? 'bg-butler-600 text-white shadow-brand-tinted'
              : 'bg-slate-100 text-ink-soft hover:bg-butler-50 hover:text-butler-700'
          }`}
        >
          <Calendar className="h-3 w-3" />
          Custom
        </button>
      </div>

      {/* Custom date inputs */}
      {showCustom && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={value.date_from || ''}
            onChange={e => onChange({ ...value, date_from: e.target.value, preset: 'custom' })}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-ink-dark"
          />
          <span className="text-xs text-ink-soft">to</span>
          <input
            type="date"
            value={value.date_to || ''}
            onChange={e => onChange({ ...value, date_to: e.target.value, preset: 'custom' })}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-ink-dark"
          />
        </div>
      )}

      {/* Time-of-day filter */}
      {showTimeFilter && (
        <div className="flex items-center gap-2 ml-2">
          <Clock className="h-3.5 w-3.5 text-ink-soft" />
          <select
            value={value.hour_from ?? ''}
            onChange={e => onChange({ ...value, hour_from: e.target.value ? Number(e.target.value) : undefined })}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-ink-dark bg-white"
          >
            <option value="">Open</option>
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
            ))}
          </select>
          <span className="text-xs text-ink-soft">–</span>
          <select
            value={value.hour_to ?? ''}
            onChange={e => onChange({ ...value, hour_to: e.target.value ? Number(e.target.value) : undefined })}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-ink-dark bg-white"
          >
            <option value="">Close</option>
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
